import { render } from 'preact';
import { IS_EYEJACK_APP } from '@webcore/core/utils/utils';
import { configureRuntime, ensureRuntimeReady } from '@core/runtime/runtime';

import { AppLoadingOverlay } from './components/AppLoadingOverlay';
import { setContentData, setExperienceId } from './contentRegistry';
import { setImmersalConfig } from './immersalConfigRegistry';
import { initSentry, Sentry } from './utils/sentry';

if (import.meta.env.DEV) {
  // import("./utils/stats").then(({ initStats }) => initStats()).catch(() => {});
  // import("./utils/eruda").then(({ initEruda }) => initEruda()).catch(() => {});
}

initSentry();

const appElement = document.getElementById('app');

// In App Clip: show loading overlay immediately (even during bootstrap/panel building) – counts as "loading model"
// AppLoadingOverlay is in main chunk; experience data + main.app load after
if (appElement && IS_EYEJACK_APP) {
  render(<AppLoadingOverlay />, appElement);
}

const EXPERIENCE_TITLES: Record<string, string> = {
  'queens-gardens': 'Queens Gardens',
  'perth-gallerists': 'Pioneering Spaces',
  'stirling-government-gardens': 'Stirling and Government Gardens',
  'harold-boas-gardens': 'Harold Boas Gardens',
  'russell-square': 'Russell Square',
};

async function bootstrap(): Promise<void> {
  const { getExperienceId, loadExperience, resolveContentForAR } = await import('./experiences');
  const experienceId = getExperienceId();

  if (EXPERIENCE_TITLES[experienceId]) {
    document.title = EXPERIENCE_TITLES[experienceId];
  }

  const { App, ContentData, immersalConfigLoader } = await loadExperience(experienceId);
  setContentData(ContentData);
  setExperienceId(experienceId);

  configureRuntime({
    load: async () => {
      const resolved = await resolveContentForAR(
        ContentData,
        immersalConfigLoader
          ? undefined
          : {
              positionMode: 'placing',
            }
      );
      setContentData(resolved);

      if (immersalConfigLoader) {
        const immersalConfig = await immersalConfigLoader();
        setImmersalConfig(immersalConfig);
        const { appReady } = await import('./main.app.immersal');
        await appReady;
        return;
      }

      const { appReady } = await import('./main.app.simple');
      await appReady;
    },
  });

  // Render microsite immediately – don't block on runtime bootstrap.
  if (appElement) {
    render(<App contentData={ContentData} />, appElement);
  }

  // Start prewarming immediately so XR permissions can be requested
  // without waiting on deferred panel resolution or main.app import.
  void ensureRuntimeReady();
}

bootstrap().catch((error) => {
  console.error('[bootstrap] Failed to initialize app', error);
  Sentry.captureException(error);
});
